import moment from 'moment';
import {
  eolsBaseApi,
  withApiErrorLoggingOnly
} from '../../utilities/api.utilities';
import { addSearchParams } from '../../utilities/app.utilities';
import {
  MedEdIsbnMetricsDto,
  MedEdQuestionsByDifficultyByPreferenceDto,
  MedEdTaxonomyCountNodeDto,
  MedEdTaxonomyFilterDto,
  MedEdTopicQuestionCountsRequestDto,
  MedEdTopicQuestionCountsResponseDto
} from './meded-app-facade-service.dtos';
import { QuestionBankProgress } from './meded-app-facade-service.models';

// The CK app asks for the whole history (the last 10000 days) and relies on the token for the user.
const METRICS_HISTORY_DAYS = 10000;

export const fetchIsbnMetrics = (courseSectionId: string): Promise<MedEdIsbnMetricsDto[]> => {
  const url = addSearchParams(`/mededtest/performance/courseSection/${encodeURIComponent(courseSectionId)}/isbn/metrics`, {
    startDate: moment().subtract(METRICS_HISTORY_DAYS, 'day').utc().toISOString(),
    endDate: moment().utc().toISOString()
  });
  return withApiErrorLoggingOnly(() => eolsBaseApi.get<MedEdIsbnMetricsDto[]>(url));
};

export const fetchTaxonomyCounts = (isbn: string): Promise<MedEdTaxonomyCountNodeDto[]> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.get<MedEdTaxonomyCountNodeDto[]>(`/mededtest/assessment/taxonomyCounts/${encodeURIComponent(isbn)}`);
  });
};

export const fetchTopicQuestionCounts = (
  payload: MedEdTopicQuestionCountsRequestDto
): Promise<MedEdTopicQuestionCountsResponseDto> => {
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post<MedEdTopicQuestionCountsResponseDto>('/mededtest/recommendation/topicQuestionCounts/filter', { data: payload });
  });
};

// Mirrors the CK app: children of a `filter: true` root become a named taxonomy filter,
// children of every other root are the topics counted directly.
export const buildTopicCountsPayload = (
  taxonomyCounts: MedEdTaxonomyCountNodeDto[] = []
): Pick<MedEdTopicQuestionCountsRequestDto, 'taxonomyTopics' | 'taxonomyFilters'> => {
  const topicIds = new Set<string>();
  const taxonomyFilters: MedEdTaxonomyFilterDto[] = [];

  taxonomyCounts.forEach((root) => {
    const childIds = (root?.children || []).map((child) => child?.vtwId).filter(Boolean);
    if (root?.filter) {
      if (childIds.length > 0) {
        taxonomyFilters.push({ name: root.displayName || root.text || '', values: childIds });
      }
    } else {
      childIds.forEach((id) => topicIds.add(id));
    }
  });

  return {
    taxonomyTopics: Array.from(topicIds),
    ...(taxonomyFilters.length > 0 ? { taxonomyFilters } : {})
  };
};

const TOTAL_QUESTION_PREFERENCES = ['neverAnswered', 'incorrect', 'correct'];

// Mirrors the CK app: the bank's total is its never-answered, incorrect and correct question counts
// summed across every difficulty level.
export const getTotalQuestions = (questionsByDifficultyByPreference: MedEdQuestionsByDifficultyByPreferenceDto | undefined): number => {
  return TOTAL_QUESTION_PREFERENCES.reduce((total, preference) => {
    const byDifficulty = questionsByDifficultyByPreference?.[preference] || {};
    return total + Object.values(byDifficulty).reduce((sum, count) => sum + (Number(count) || 0), 0);
  }, 0);
};

// Answered and correct come from the bank's performance metrics, the total from its question counts,
// as in the CK app. The metrics can count repeated attempts, so answered is capped at the total and
// correct at answered; that keeps incorrect and unanswered from going negative. A bank without a
// metrics entry has no activity.
export const buildQuestionBankProgress = (
  isbn: string,
  metrics: MedEdIsbnMetricsDto | undefined,
  questionsByDifficultyByPreference: MedEdQuestionsByDifficultyByPreferenceDto | undefined
): QuestionBankProgress => {
  const total = getTotalQuestions(questionsByDifficultyByPreference);
  const answered = Math.min(Math.max(Number(metrics?.performance?.totalAnswered) || 0, 0), total);
  const correct = Math.min(Math.max(Number(metrics?.performance?.totalCorrect) || 0, 0), answered);

  return {
    isbn,
    total,
    answered,
    correct,
    incorrect: answered - correct,
    unanswered: total - answered
  };
};
